import React, { useEffect, useState } from "react";
import DailyLogWitness from "./dailyLogForms/daily-log-witness";
import BehaviorSelection from "./dailyLogForms/behavioural-select";
import ChallengingBehavior from "./dailyLogForms/challenging-behaviour";
import DeescalationStrategies from "./dailyLogForms/de-escalation-strategy";
import PhysicalIntervention from "./dailyLogForms/physical-intervension";
import VerbalWarning from "./dailyLogForms/verbal-warning";
import axios from "axios";
import IncidentManagement from "./dailyLogForms/incident-management";
import { useNavigate } from "react-router-dom";

const DailyLogIncident = ({ taskId }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(null); // initialized as null
  const [manager, setManager] = useState(null);
  const [formData, setFormData] = useState(null);

  // Load saved form data and step on initial mount
  useEffect(() => {
    const savedData = localStorage.getItem(`dailyLogFormData-${taskId}`);
    const savedStep = localStorage.getItem(`dailyLogCurrentStep-${taskId}`);

    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      } catch (err) {
        console.error("Error parsing saved form data:", err);
      }
    } else {
      // Initialize blank structure if no saved data
      setFormData({
        incidentManagementInfo: {},
        witnessInfo: {},
        behaviouralInfo: {},
        challengingInfo: {},
        strategyInfo: {},
        physicalInfo: {},
        verbalWarningInfo: {},
      });
    }

    if (savedStep !== null) {
      setCurrentStep(parseInt(savedStep, 10));
    } else {
      setCurrentStep(0);
    }

    const fetchUsers = async () => {
      try {
        const response = await axios.get("/api/auth/users");
        const lineManager = response.data.find((user) => user.roleid === 3);
        setManager(lineManager || null);
      } catch (error) {
        console.error("Error fetching manager:", error);
      }
    };

    fetchUsers();
  }, [taskId]);

  useEffect(() => {
    // Save to localStorage whenever formData or currentStep changes
    if (formData && currentStep !== null) {
      localStorage.setItem(`dailyLogFormData-${taskId}`, JSON.stringify(formData));
      localStorage.setItem(`dailyLogCurrentStep-${taskId}`, currentStep.toString());
    }
  }, [formData, currentStep, taskId]);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const updateFormData = (newData) => {
    setFormData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const updateDailyLogStatus = async () => {
    try {
      await axios.put(`/api/dailyLog/log/${taskId}`, {
        status: "completed",
      });
    } catch (error) {
      console.error("Error updating daily log status:", error);
      alert("Failed to update daily log status.");
    }
  };

  const handleCreateStaffDebrief = async (incidentId) => {
    try {
      const todayDate = new Date().toISOString().split("T")[0];
      await axios.post("/api/staff/staffDebrief", {
        witnessId: manager.id,
        taskName: `Staff Debrief ${todayDate}`,
        incidentId,
      });
    } catch (err) {
      alert(err.response?.data?.error || "Something went wrong!");
    }
  };

  const handleCreateChildDebrief = async (incidentId) => {
    try {
      const todayDate = new Date().toISOString().split("T")[0];
      await axios.post("/api/child/childDebrief", {
        witnessId: manager.id,
        taskName: `Child Debrief ${todayDate}`,
        incidentId,
      });
    } catch (err) {
      alert(err.response?.data?.error || "Something went wrong!");
    }
  };

  const handleCreateSupport = async (incidentId) => {
    try {
      const todayDate = new Date().toISOString().split("T")[0];
      const witnessIds = formData?.witnessInfo?.map((item) => item.userId) || [];

      if (!witnessIds.length) return;

      for (const witnessId of witnessIds) {
        await axios.post("/api/support/supportStatement", {
          witnessId,
          taskName: `Support statement ${todayDate}`,
          incidentId,
        });
      }
    } catch (err) {
      alert(err.response?.data?.error || "Something went wrong!");
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(
        "/api/dailyLogIncident/incident",
        { ...formData, dailyLogId: taskId },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const createdIncidentId = response?.data?.id;
      if (!createdIncidentId) throw new Error("Incident ID not returned.");

      await handleCreateSupport(createdIncidentId);
      await handleCreateChildDebrief(createdIncidentId);
      await handleCreateStaffDebrief(createdIncidentId);
      await updateDailyLogStatus();

      localStorage.removeItem(`dailyLogFormData-${taskId}`);
      localStorage.removeItem(`dailyLogCurrentStep-${taskId}`);

      navigate("/daily-logs");
    } catch (err) {
      console.log(err.response?.data?.error || "Something went wrong!");
    }
  };

  const renderForm = () => {
    if (formData === null || currentStep === null) return <div>Loading...</div>;

    switch (currentStep) {
      case 0:
        return (
          <IncidentManagement
            updateFormData={updateFormData}
            nextStep={nextStep}
            formData={formData.incidentManagementInfo}
          />
        );
      case 1:
        return (
          <DailyLogWitness
            updateFormData={updateFormData}
            prevStep={prevStep}
            nextStep={nextStep}
            formData={formData.witnessInfo}
          />
        );
      case 2:
        return (
          <BehaviorSelection
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            formData={formData.behaviouralInfo}
          />
        );
      case 3:
        return (
          <ChallengingBehavior
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            formData={formData.challengingInfo}
            temp={formData?.temperature}
            volume={formData?.sound}
            person={formData?.whoWasAround}
          />
        );
      case 4:
        return (
          <DeescalationStrategies
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            formData={formData.strategyInfo}
          />
        );
      case 5:
        return (
          <PhysicalIntervention
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            formData={formData.physicalInfo}
          />
        );
      case 6:
        return (
          <VerbalWarning
            updateFormData={updateFormData}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
            formData={formData.verbalWarningInfo}
            verbal={formData.clearVerbal}
          />
        );
      default:
        return null;
    }
  };

  return <div>{renderForm()}</div>;
};

export default DailyLogIncident;
